import { api, ApiError } from './client.js';
import { confidenceFor } from './forecastText.js';

/*
 * Platform endpoints — forecasts, Top Picks, history.
 *
 * These now accept the web JWT (dual auth: Telegram initData OR Bearer token),
 * so everything here is a real call. Nothing in this file is mocked.
 *
 *   GET  /api/sports
 *   GET  /api/dates/{sport}
 *   GET  /api/leagues/{sport}/{date}
 *   GET  /api/matches/{sport}/{date}/{league}
 *   POST /api/forecast
 *   GET  /api/top-picks          403 unless Premium
 *   GET  /api/history            {locked: true} unless on a paid plan
 */

/* ------------------------------------------------------------------ *
 * Wizard steps
 * ------------------------------------------------------------------ */

/** @returns [{key, name, emoji}] */
export async function getSports() {
  const res = await api.get('/api/sports', { auth: true });
  return res.sports ?? [];
}

/**
 * @returns [{key}] where key is 'YYYY-MM-DD'
 *
 * The endpoint returns bare date strings — no labels and no match counts — so
 * "Today"/"Tomorrow" is worked out here from the user's own clock.
 */
export async function getDates(sport) {
  const res = await api.get(`/api/dates/${encodeURIComponent(sport)}`, { auth: true });
  return (res.dates ?? []).map((key) => ({ key, date: parseApiDate(key) }));
}

/** @returns [{key, name}] — no match counts available */
export async function getLeagues(sport, date) {
  const res = await api.get(
    `/api/leagues/${encodeURIComponent(sport)}/${encodeURIComponent(date)}`,
    { auth: true }
  );
  return res.leagues ?? [];
}

/** @returns [{id, home, away, time, time_utc, league}] */
export async function getMatches(sport, date, league) {
  const res = await api.get(
    `/api/matches/${encodeURIComponent(sport)}/${encodeURIComponent(date)}/${encodeURIComponent(
      league
    )}`,
    { auth: true }
  );
  return res.matches ?? [];
}

/* ------------------------------------------------------------------ *
 * The forecast
 * ------------------------------------------------------------------ */

/**
 * Generate a forecast.
 *
 * Returns the raw `forecast` string; the caller parses it with
 * parseForecast() from ./forecastText.js.
 *
 * Free accounts are rate limited to one forecast every three days. That comes
 * back as a 429 whose detail is {type:'cooldown', time_remaining, next_at} —
 * see isCooldownError() below, which callers use to show the waiting message
 * instead of a generic failure.
 */
export async function generateForecast({ sport, league, match }) {
  const res = await api.post('/api/forecast', {
    auth: true,
    body: {
      sport: sport.key,
      league_key: league.key,
      league_name: league.name,
      // The API declares match_id as a string; fixture ids arrive as numbers.
      match_id: String(match.id),
      home_team: match.home,
      away_team: match.away,
      match_time_utc: match.time_utc,
    },
  });
  return res.forecast ?? '';
}

/**
 * Is this the free-tier cooldown rather than a real failure?
 * @returns {null | {timeRemaining: string, nextAt: string|null}}
 */
export function cooldownFrom(error) {
  if (!(error instanceof ApiError) || error.status !== 429) return null;
  const detail = error.detail;
  if (!detail || detail.type !== 'cooldown') return null;
  return {
    timeRemaining: detail.time_remaining ?? '',
    nextAt: detail.next_at ?? null,
  };
}

/* ------------------------------------------------------------------ *
 * Top Picks — Premium only
 * ------------------------------------------------------------------ */

/**
 * Today's Top Picks.
 *
 * The endpoint 403s for anyone without Premium — there is no partial access, so
 * a 403 means "show the upgrade prompt", not "an error occurred".
 *
 * @returns {{picks: Array, locked: boolean, appliedFilters: object|null}}
 */
export async function getTopPicks() {
  try {
    const res = await api.get('/api/top-picks', { auth: true });
    return {
      picks: (res.picks ?? []).map(normalisePick),
      locked: false,
      appliedFilters: res.applied_filters ?? null,
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 403) {
      return { picks: [], locked: true, appliedFilters: null };
    }
    throw err;
  }
}

/**
 * The API gives `match_name` ("Man City vs Arsenal") plus the winner's
 * probability — not per-side numbers. Split the name so the two sides can be
 * labelled, and put the probability on whichever side is the predicted winner.
 */
function normalisePick(p) {
  const [home, away] = splitMatchName(p.match_name);
  const winnerPct = clampPct(p.win_probability);
  const winnerIsHome = !p.predicted_winner || isSameSide(p.predicted_winner, home);

  return {
    id: p.id,
    sport: p.sport,
    league: p.league_name,
    matchName: p.match_name,
    home,
    away,
    // The split bar takes the home side's share.
    homeProbability: winnerPct === null ? null : winnerIsHome ? winnerPct : 100 - winnerPct,
    winner: winnerIsHome ? 'home' : 'away',
    predictedWinner: p.predicted_winner || (winnerIsHome ? home : away),
    confidence: winnerPct === null ? 'low' : confidenceFor(winnerPct),
    // `match_date` is a calendar DATE ("2026-08-06") — a day, not an instant, so
    // it can't produce a kick-off time. `match_start_utc` is the real instant,
    // carries a Z offset, and may be null when the fixture time is unknown.
    matchDate: p.match_date ?? null,
    matchStartUtc: p.match_start_utc ?? null,
    forecastText: p.forecast_text ?? '',
  };
}

/* ------------------------------------------------------------------ *
 * History — paid plans only
 * ------------------------------------------------------------------ */

/**
 * Forecast history.
 *
 * Free accounts get `{locked: true, history: []}` rather than a 403, so the
 * screen shows an upgrade prompt instead of an error.
 *
 * Grading is optional per row: `result` is 'correct' | 'incorrect' | 'void' |
 * null, `actual_winner` names the side that won, and `graded_at` is when it was
 * settled. A row where `result` is absent entirely predates grading and shows no
 * outcome; a row where it is present but null is awaiting its result.
 */
export async function getHistory() {
  const res = await api.get('/api/history', { auth: true });
  return {
    locked: Boolean(res.locked),
    items: (res.history ?? []).map((row) => ({
      id: row.id,
      sport: row.sport,
      league: row.league,
      matchName: row.match_name,
      matchDate: row.match_date ?? null,
      generatedAt: row.generated_at ?? null,
      forecastText: row.forecast_text ?? '',
      // `'result' in row` distinguishes "not graded yet" (present, null) from
      // "this deployment doesn't grade" (absent) — the two want different UI.
      isGradeable: 'result' in row,
      result: row.result ?? null,
      actualWinner: row.actual_winner ?? null,
      gradedAt: row.graded_at ?? null,
    })),
  };
}

/* ------------------------------------------------------------------ *
 * helpers
 * ------------------------------------------------------------------ */

/** 'YYYY-MM-DD' as a local-midnight Date (not UTC, which can shift the day). */
function parseApiDate(value) {
  const [y, m, d] = String(value).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** "A vs B" -> ['A', 'B']. Falls back to the whole string as the home side. */
function splitMatchName(name = '') {
  const parts = String(name).split(/\s+(?:vs\.?|v\.?|—|-)\s+/i);
  if (parts.length >= 2) return [parts[0].trim(), parts.slice(1).join(' ').trim()];
  return [String(name).trim(), ''];
}

function isSameSide(a = '', b = '') {
  const norm = (s) => s.toLowerCase().replace(/[^a-z0-9Ѐ-ӿ]/g, '');
  const x = norm(a);
  const y = norm(b);
  return Boolean(x && y) && (x === y || x.includes(y) || y.includes(x));
}

function clampPct(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.max(0, Math.min(100, Math.round(n)));
}
